import { OverlayModule } from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { NgModule, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgbModule, NgbTypeaheadModule } from '@ng-bootstrap/ng-bootstrap';
import { EffectsModule, provideEffects } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { provideStore, StoreModule } from '@ngrx/store';
import { provideStoreDevtools, StoreDevtoolsModule } from '@ngrx/store-devtools';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { I18nService, LanguageInterceptor } from './core/i18n';
import { NgxPermissionsModule } from 'ngx-permissions';
import { provideToastr } from '@iqx-limited/ngx-toastr';
import { IdentityModule } from './core/identity/identity.module';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app.routes.module';
import { AuthEffects, AuthService, RoleGuard } from './core/auth';
import { LayoutConfig } from './core/config/layout.config';
import { CoreModule } from './core/core.module';
import { metaReducers, reducers } from './core/core.state';
import { ErrorPageComponent } from './core/error-page/error-page.component';
import { ChatFlowsService } from './core/services/chatflows.service';
import { CoreService } from './core/services/core.service';
import { SavedRepliesService } from './core/services/saved.replies.service';
import { ChatFlowsEffects } from './core/store/chatflow.effects';
import { LayoutEffects } from './core/store/layout.effects';
import { PropertiesEffects } from './core/store/properties.effects';
import { SavedRepliesEffects } from './core/store/saved.replies.effects';
import { ToastrEffects } from './core/store/toastr.effects';
import {
	DataTableService, KtDialogService, LayoutConfigService,
	LayoutRefService, MenuAsideService, MenuConfigService,
	PageConfigService, SplashScreenService
} from './core/_base/layout';
import { FallbackComponent } from './fallback.component';
import { SettingsService } from './settings/services/settings.service';
import { SettingsEffects } from './settings/store/settings.effects';
import { ShouldLoginComponent } from './should-login.component';
import { NgxGoogleAnalyticsModule, NgxGoogleAnalyticsRouterModule } from 'ngx-google-analytics';
import { environment } from '../environments/environment';
import { KbEffects } from './kb/store/kb.effects';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { VisitorsEffects } from './inbox/store/visitors.effects';
import { ActivityEffects } from './inbox/store/activity.effects';
import { TicketsEffects } from './inbox/store/tickets.effects';
import { MarkdownModule, MARKED_OPTIONS } from 'ngx-markdown';
import { markedOptionsFactory } from './core/markedOptionsFactory';

export function initializeLayoutConfig(appConfig: LayoutConfigService) {
	// initialize app by loading default demo layout config
	return () => {
		if (appConfig.getConfig() === null) {
			appConfig.loadConfigs(new LayoutConfig().configs);
		}
	};
}

@NgModule({ declarations: [
        AppComponent,
        ErrorPageComponent,
        FallbackComponent,
        ShouldLoginComponent
    ],
    bootstrap: [AppComponent], 
    imports: [
        BrowserAnimationsModule,
        /*BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),*/
        BrowserModule,
        IdentityModule.forRoot(),
        NgxPermissionsModule.forRoot(),
        CoreModule,
        OverlayModule,
        //StoreModule.forRoot(reducers, {metaReducers}),
        EffectsModule.forRoot([
            AuthEffects,
            SavedRepliesEffects,
            ChatFlowsEffects,
            PropertiesEffects,
            ToastrEffects,
            LayoutEffects,
            /* Needed on the app.module and not on the settings.module
            since the settingseffects loads the properties key  */
            SettingsEffects,
            // need by both conversations module and live visitors module
            VisitorsEffects,
            KbEffects
        ]),
        StoreRouterConnectingModule.forRoot({ stateKey: 'router' }),
        //StoreDevtoolsModule.instrument(),
        FormsModule,
        //AuthModule.forRoot(),
        NgbModule, NgbTypeaheadModule,
        MonacoEditorModule.forRoot({
            baseUrl: window.location.origin + "/assets/monaco/min/vs"
        }),
        NgxGoogleAnalyticsModule.forRoot(environment.ga),
        NgxGoogleAnalyticsRouterModule,
        /* AppRouting needs to be the last after all other routings modules */
        AppRoutingModule
    ], 
    providers: [
        /*{
            provide: HTTP_INTERCEPTORS,
            useClass: AuthorizeInterceptor,
            multi: true ,
            deps: [AuthorizeService, Router, Store]
        },*/
        AuthService,
        SettingsService,
        SavedRepliesService,
        ChatFlowsService,
        CoreService,
        LayoutConfigService,
        LayoutRefService,
        MenuConfigService,
        PageConfigService,
        KtDialogService,
        DataTableService,
        SplashScreenService,
        provideAppInitializer(() => {
        const initializerFn = (initializeLayoutConfig)(inject(LayoutConfigService));
        return initializerFn();
      }),
        // template services
        MenuAsideService,
        RoleGuard,
        provideTranslateService(),
        provideTranslateHttpLoader({ prefix: 'assets/i18n/', suffix: '.json' }),
        provideAppInitializer(() => inject(I18nService).init()),
        { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
        provideToastr({
            positionClass: 'toast-bottom-center'
        }),
        provideStore(reducers, { metaReducers }),
        provideEffects([ActivityEffects, VisitorsEffects, TicketsEffects]),
        provideStoreDevtools(),
        provideHttpClient(withXhr(), withInterceptorsFromDi()),
        importProvidersFrom(MarkdownModule.forRoot({
            markedOptions: {
                provide: MARKED_OPTIONS,
                useFactory: markedOptionsFactory,
            },
            }))
    ] })
export class AppModule { }
